import { useEffect, useRef, useState } from "react";
import { useAppDispatch } from "@store/index";
import { useGetDatasetQuestionsResponseMutation } from "@store/api/dataset-api";
import type {
  ScorecardQuestionRow,
  ScorecardRollingAverageConfig,
  ScorecardTimePeriodSelection,
  ScorecardWeightSelection,
} from "@models/Scorecard";
import { DEFAULT_VISUALIZATION_BASE } from "@models/VisualizationBase";
import { resolveVisualizationBaseFilters } from "@utils/visualization-base-search";
import { resolveTimePeriodDates } from "@utils/time-period-utils";
import {
  buildRowFetchPlan,
  extractCellFromData,
  extractDiagonalCellFromData,
  getBaseFilterSignature,
  getBaseMappingForBrand,
  getCrossFocusParamsForBaseRow,
  getFocusParamsForRow,
  type ChartData,
  type ScorecardCellValue,
  type ScorecardRowFetchPlan,
} from "@utils/scorecard-fetch-utils";

export type { ScorecardCellValue } from "@utils/scorecard-fetch-utils";

type RowCellMap = Record<string, ScorecardCellValue>;

export function useScorecardQuestionRowData(
  datasetId: string | null,
  rows: ScorecardQuestionRow[],
  weight: ScorecardWeightSelection = "DISABLED",
  timePeriodSelection: ScorecardTimePeriodSelection = {
    timePeriod: "all_time",
    startDate: null,
    endDate: null,
  },
  rollingAverage: ScorecardRollingAverageConfig = { enabled: false, windowSize: 2 },
  // When a data availability preview is active, the [start, end] range to apply to
  // an "all_time" selection (earliest response date → preview cutoff). All_time
  // otherwise sends no dates, so without this the preview ceiling is ignored for
  // scorecards; both dates are sent together so the range is never half-open.
  allTimePreviewRange: { startDate: string; endDate: string } | null = null,
) {
  const dispatch = useAppDispatch();
  const [getDatasetQuestionsResponse] = useGetDatasetQuestionsResponseMutation();

  // Keyed by `${sourceQuestionId}::${resolved filter signature}::${focus signature}::${cross signature}`.
  const [dataByFetchKey, setDataByFetchKey] = useState<Record<string, ChartData>>({});
  const [loadingFetchKeys, setLoadingFetchKeys] = useState<Set<string>>(new Set());
  // Which real fetch key each row ultimately resolved to — known only after
  // its (async) filter resolution completes.
  const [rowFetchKey, setRowFetchKey] = useState<Record<string, string>>({});
  // The fetch shape/plan each row resolved to — needed at read time to pick
  // flat vs. diagonal cell extraction.
  const [rowFetchPlan, setRowFetchPlan] = useState<Record<string, ScorecardRowFetchPlan>>({});
  const [resolvingRowIds, setResolvingRowIds] = useState<Set<string>>(new Set());

  // Fetches already fired, keyed by a date-INCLUSIVE fetchKey (see below), so an
  // identical request is never re-fired even if requestKey oscillates through a
  // transient during a preview change/end; the result stays cached in dataByFetchKey.
  const fetchedKeysRef = useRef<Set<string>>(new Set());
  // Resolved visualization-base filters cached per date-INDEPENDENT row config, so a
  // date/preview change re-fetches (new fetchKey) without re-resolving the filters.
  const resolvedFiltersRef = useRef<
    Map<string, Awaited<ReturnType<typeof resolveVisualizationBaseFilters>>>
  >(new Map());
  // Synchronous mirror of each row's current fetchKey — lets fireWithFilters bail
  // (no setState) when nothing changed, since the effect re-runs every render.
  const rowFetchKeyRef = useRef<Record<string, string>>({});
  // Config keys whose filter resolution is currently in flight — prevents
  // re-entering the async (and its setResolvingRowIds) on every render while the
  // resolve is still pending, which would otherwise loop synchronously forever.
  const resolvingConfigKeysRef = useRef<Set<string>>(new Set());

  const requestKey = [
    weight,
    timePeriodSelection.timePeriod,
    timePeriodSelection.startDate,
    timePeriodSelection.endDate,
    rollingAverage.enabled ? rollingAverage.windowSize : 0,
    allTimePreviewRange
      ? `${allTimePreviewRange.startDate}:${allTimePreviewRange.endDate}`
      : "",
  ].join("|");

  useEffect(() => {
    if (!datasetId) return;

    const resolvedPeriod = resolveTimePeriodDates(timePeriodSelection);
    const isAllTime = resolvedPeriod.timePeriod === "all_time";
    const shouldIncludeDates =
      !isAllTime && !!resolvedPeriod.startDate && !!resolvedPeriod.endDate;

    const commonParams = {
      weightSelection: weight,
      topN: null as number | null,
      sorting: "ORIGINAL" as const,
      ...(shouldIncludeDates && {
        startDate: resolvedPeriod.startDate!,
        endDate: resolvedPeriod.endDate!,
      }),
      // All Time under an active preview: apply the full [earliest, cutoff] range.
      ...(isAllTime &&
        allTimePreviewRange && {
          startDate: allTimePreviewRange.startDate,
          endDate: allTimePreviewRange.endDate,
        }),
      ...(rollingAverage.enabled &&
        shouldIncludeDates && { periodCount: rollingAverage.windowSize }),
    };

    rows.forEach((row) => {
      const visualizationBase = row.visualizationBase ?? DEFAULT_VISUALIZATION_BASE;

      // Date-INDEPENDENT config key for caching the row's resolved base filters.
      // Beyond id/sourceQuestionId/visualizationBase/rowBase, this folds in the
      // row's own focus/rollup selection and, when its Base is an Existing
      // Question Row, that referenced row's focus/rollup too, so edits re-resolve.
      const rowBase = row.rowBase;
      const baseRow =
        rowBase?.kind === "existing_question_row"
          ? rows.find((r) => r.id === rowBase.questionRowId)
          : undefined;
      const configKey = [
        row.id,
        row.sourceQuestionId,
        JSON.stringify(visualizationBase),
        JSON.stringify(row.rowBase ?? null),
        JSON.stringify(getFocusParamsForRow(row)),
        baseRow?.sourceQuestionId ?? "",
        baseRow ? JSON.stringify(getCrossFocusParamsForBaseRow(baseRow)) : "",
      ].join("::");

      const fireWithFilters = (
        baseFilters: Awaited<ReturnType<typeof resolveVisualizationBaseFilters>>,
      ) => {
        const plan = buildRowFetchPlan(row, rows, baseFilters);
        // fetchKey is date-INCLUSIVE (requestKey): distinct dates are distinct
        // requests, identical ones are fetched once and served from cache.
        const fetchKey = [
          plan.questionId,
          getBaseFilterSignature(plan.filters),
          JSON.stringify(plan.focusParams),
          plan.crossQuestionId ?? "",
          JSON.stringify(plan.crossFocusParams ?? {}),
          requestKey,
        ].join("::");

        // Idempotent: if this row already points at this fetchKey there is nothing
        // to update or fetch — bail before any setState to avoid a render loop.
        if (rowFetchKeyRef.current[row.id] === fetchKey) return;
        rowFetchKeyRef.current = {
          ...rowFetchKeyRef.current,
          [row.id]: fetchKey,
        };
        setRowFetchKey((prev) => ({ ...prev, [row.id]: fetchKey }));
        setRowFetchPlan((prev) => ({ ...prev, [row.id]: plan }));

        if (fetchedKeysRef.current.has(fetchKey)) return;
        fetchedKeysRef.current.add(fetchKey);

        setLoadingFetchKeys((prev) => new Set(prev).add(fetchKey));
        getDatasetQuestionsResponse({
          id: plan.questionId,
          datasetId,
          chartType: "SCORECARD",
          filters: plan.filters.length > 0 ? plan.filters : undefined,
          ...plan.focusParams,
          ...(plan.crossQuestionId && { crossQuestionId: plan.crossQuestionId }),
          ...plan.crossFocusParams,
          ...commonParams,
        })
          .unwrap()
          .then((result) => {
            setDataByFetchKey((prev) => ({ ...prev, [fetchKey]: result.data ?? [] }));
          })
          .catch(() => {
            setDataByFetchKey((prev) => ({ ...prev, [fetchKey]: [] }));
          })
          .finally(() => {
            setLoadingFetchKeys((prev) => {
              const next = new Set(prev);
              next.delete(fetchKey);
              return next;
            });
          });
      };

      const cachedFilters = resolvedFiltersRef.current.get(configKey);
      if (cachedFilters) {
        fireWithFilters(cachedFilters);
        return;
      }

      // Resolve filters once per config; skip if already in flight so repeated
      // renders during the pending async don't re-trigger it (infinite loop).
      if (resolvingConfigKeysRef.current.has(configKey)) return;
      resolvingConfigKeysRef.current.add(configKey);
      setResolvingRowIds((prev) =>
        prev.has(row.id) ? prev : new Set(prev).add(row.id),
      );
      (async () => {
        const baseFilters = await resolveVisualizationBaseFilters(
          dispatch,
          datasetId,
          visualizationBase,
        );
        resolvedFiltersRef.current.set(configKey, baseFilters);
        resolvingConfigKeysRef.current.delete(configKey);
        setResolvingRowIds((prev) => {
          if (!prev.has(row.id)) return prev;
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
        fireWithFilters(baseFilters);
      })();
    });
  }, [
    datasetId,
    rows,
    getDatasetQuestionsResponse,
    requestKey,
    timePeriodSelection,
    rollingAverage,
    weight,
    allTimePreviewRange,
    dispatch,
  ]);

  const getRowCells = (row: ScorecardQuestionRow): RowCellMap => {
    const cells: RowCellMap = {};
    const fetchKey = rowFetchKey[row.id];
    const data = fetchKey ? dataByFetchKey[fetchKey] : undefined;
    if (!data) return cells;
    const plan = rowFetchPlan[row.id];
    row.brandMappings.forEach((mapping) => {
      if (!mapping.boundResponseLabel) return;
      const cell =
        plan?.fetchShape === "crosstab_diagonal"
          ? extractDiagonalCellFromData(
              data,
              mapping,
              getBaseMappingForBrand(row, mapping.brandId, rows),
            )
          : extractCellFromData(data, mapping);
      if (cell) cells[mapping.brandId] = cell;
    });
    return cells;
  };

  const isRowLoading = (row: ScorecardQuestionRow): boolean => {
    if (resolvingRowIds.has(row.id)) return true;
    const fetchKey = rowFetchKey[row.id];
    return !!fetchKey && loadingFetchKeys.has(fetchKey);
  };

  return { getRowCells, isRowLoading };
}
